import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputSwitch } from "primereact/inputswitch";
import { useNavigate, useLocation } from "react-router-dom";
import Layout from "../Common/Layout";
import axios from "axios";
import { Toast } from "primereact/toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDate, formatDateOfBirth } from "../utils/dateFormat";
// Reusable input/select fields for driver details

function LicenseStatusField({
  value,
  onChange,
  name = "licenseStatus",
  selectedState,
  error = "",
}: {
  value: string;
  onChange: (e: any) => void;
  name?: string;
  selectedState?: string;
  error?: string;
}) {
  const LICENSE_STATUS_ALABAMA = ["Valid", "Expired", "Revoked", "Suspended"];
  const LICENSE_STATUS_OHIO = ["Valid", "Unlicensed", "Permit", "Suspended"];
  const LICENSE_STATUS_FLORIDA = ["Valid", "Unlicensed", "Permit", "Expired", "Revoked", "Suspended"];
  const LICENSE_STATUS_GEORGIA = ["Valid", "Unlicensed", "Permit", "Expired", "Revoked", "Suspended"];
  const LICENSE_STATUS_TENNESSEE = ["Valid", "Unlicensed", "Permit", "Suspended"];
  const LICENSE_STATUS_SOUTH_CAROLINA = ["Valid", "Unlicensed", "Permit", "Expired", "Revoked", "Suspended"];
  const LICENSE_STATUS_DEFAULT = ["Valid", "Unlicensed", "Permit", "Suspended", "Revoked", "Expired", "Foreign", "Restricted"];

  const licenseStatuses =
    selectedState === "Ohio"
      ? LICENSE_STATUS_OHIO
      : selectedState === "Alabama"
        ? LICENSE_STATUS_ALABAMA
        : selectedState === "Florida"
          ? LICENSE_STATUS_FLORIDA
          : selectedState === "Georgia"
            ? LICENSE_STATUS_GEORGIA
            : selectedState === "Tennessee"
              ? LICENSE_STATUS_TENNESSEE
              : selectedState === "South Carolina"
                ? LICENSE_STATUS_SOUTH_CAROLINA
                : LICENSE_STATUS_DEFAULT;

  return (
    <div className="form-group">
      <label>
        License Status <span className="text-danger">*</span>
      </label>
      <select name={name} className={`form-input compact-select${error ? " error" : ""}`} value={value} onChange={onChange}>
        <option value="">Select status</option>
        {licenseStatuses.map((status) => (
          <option key={status} value={status}>
            {status}
          </option>
        ))}
      </select>
      {error && <span className="error-message">{error}</span>}
    </div>
  );
}

function InternationalDriversLicenseField({
  value,
  onChange,
  name = "internationalDriversLicense",
}: {
  value: string;
  onChange: (e: any) => void;
  name?: string;
}) {
  return (
    <div className="form-group">
      <label>International Driver's License</label>
      <select name={name} className="form-input compact-select" value={value} onChange={onChange}>
        <option value="">Select</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
      </select>
    </div>
  );
}

function MonthsEmployedField({ value, onChange, name = "monthsEmployed" }: { value: string | number; onChange: (e: any) => void; name?: string }) {
  return (
    <div className="form-group">
      <label>Months Employed</label>
      <input type="number" name={name} className="form-input" placeholder="Enter months employed" value={value} onChange={onChange} />
    </div>
  );
}

// Reusable select field for 'Country Of Origin'
function CountryOfOriginField({
  value,
  onChange,
  name = "countryOfOrigin",
  required = false,
  error = "",
}: {
  value: string;
  onChange: (e: any) => void;
  name?: string;
  required?: boolean;
  error?: string;
}) {
  return (
    <div className="form-group">
      <label>Country Of Origin{required && <span className="text-danger"> *</span>}</label>
      <select name={name} className={`form-input compact-select ${error ? "error" : ""}`} value={value} onChange={onChange}>
        <option value="">Select country</option>
        <option value="None">None</option>
        <option value="International">International</option>
        <option value="Canada">Canada</option>
        <option value="Mexico">Mexico</option>
        <option value="Poland">Poland</option>
        <option value="Matricula">Matricula</option>
        <option value="Other">Other</option>
      </select>
      {error && <span className="error-message">{error}</span>}
    </div>
  );
}

const EDUCATION_OPTIONS_UNIFIED = [
  "None",
  "Bachelors Degree",
  "Masters Degree",
  "Doctorate Degree",
  "Medical Degree",
  "Law Degree",
  "No High School Diploma",
  "High School Diploma",
  "Associates Degree",
  "Some College",
  "Vocational Degree",
];

const EDUCATION_API_TO_UI_MAP: Record<string, string> = {
  "No High School Diploma": "No High School Diploma",
  "High School Diploma": "High School Diploma",
  "Some College - No Degree": "Some College",
  "Vocational - Technical Degree": "Vocational Degree",
  "Associates Degree": "Associates Degree",
  Bachelors: "Bachelors Degree",
  Masters: "Masters Degree",
  PhD: "Doctorate Degree",
  "Medical Degree": "Medical Degree",
  "Law Degree": "Law Degree",
};

const occupationOptions = [
  { name: "Accountant/Auditor", value: "Accountant/Auditor" },
  { name: "Administrative Assistant", value: "Administrative Assistant" },
  { name: "Agent", value: "Agent" },
  { name: "Agent/Broker", value: "Agent/Broker" },
  { name: "Assistant-Medic/Dent/Vet", value: "Assistant-Medic/Dent/Vet" },
  { name: "Attorney", value: "Attorney" },
  { name: "Bartender", value: "Bartender" },
  { name: "Bookkeeper", value: "Bookkeeper" },
  { name: "Caregiver", value: "Caregiver" },
  { name: "Cashier/Checker", value: "Cashier/Checker" },
  { name: "Child/Daycare Worker", value: "Child/Daycare Worker" },
  { name: "Claims Adjuster", value: "Claims Adjuster" },
  { name: "Clergy", value: "Clergy" },
  { name: "Clerk-Office", value: "Clerk-Office" },
  { name: "Client Care Worker", value: "Client Care Worker" },
  { name: "Construction Worker", value: "Construction Worker" },
  { name: "Construction-Project Manager", value: "Construction-Project Manager" },
  { name: "Contractor", value: "Contractor" },
  { name: "Cook/Worker-Fast Food", value: "Cook/Worker-Fast Food" },
  { name: "Cook-Restaurant/Cafeteria", value: "Cook-Restaurant/Cafeteria" },
  { name: "Corrections Officer", value: "Corrections Officer" },
  { name: "CSR/Teller", value: "CSR/Teller" },
  { name: "Custodian/Janitor", value: "Custodian/Janitor" },
  { name: "Customer Service Representative", value: "Customer Service Representative" },
  { name: "Delivery Person", value: "Delivery Person" },
  { name: "Director/Administrator", value: "Director/Administrator" },
  { name: "Doctor", value: "Doctor" },
  { name: "Driver-Truck/Delivery", value: "Driver-Truck/Delivery" },
  { name: "Engineer", value: "Engineer" },
  { name: "Enlisted Military Personnel(E1-E4)", value: "Enlisted Military Personnel(E1-E4)" },
  { name: "Executive", value: "Executive" },
  { name: "Factory Worker", value: "Factory Worker" },
  { name: "Fire Fighter/Supervisor", value: "Fire Fighter/Supervisor" },
  { name: "Foreman/Supervisor", value: "Foreman/Supervisor" },
  { name: "Hair Stylist/Barber", value: "Hair Stylist/Barber" },
  { name: "Handyman", value: "Handyman" },
  { name: "Heat/Air Technician", value: "Heat/Air Technician" },
  { name: "Homemaker(full-time)", value: "Homemaker(full-time)" },
  { name: "Housekeeper/Maid", value: "Housekeeper/Maid" },
  { name: "Laborer/Worker", value: "Laborer/Worker" },
  { name: "Manager", value: "Manager" },
  { name: "Mechanic", value: "Mechanic" },
  { name: "Nurse-C.N.A.", value: "Nurse-C.N.A." },
  { name: "Nurse-RN", value: "Nurse-RN" },
  { name: "Officer-Commissioned", value: "Officer-Commissioned" },
  { name: "Officer-Warrant", value: "Officer-Warrant" },
  { name: "Other", value: "Other" },
  { name: "Painter", value: "Painter" },
  { name: "Pilot/Captain/Engineer", value: "Pilot/Captain/Engineer" },
  { name: "Plumber", value: "Plumber" },
  { name: "Police Detective/Investigator", value: "Police Detective/Investigator" },
  { name: "Police Officer/Supervisor", value: "Police Officer/Supervisor" },
  { name: "Principal", value: "Principal" },
  { name: "Professor College", value: "Professor College" },
  { name: "Programmer", value: "Programmer" },
  { name: "Realtor", value: "Realtor" },
  { name: "Receptionist/Secretary", value: "Receptionist/Secretary" },
  { name: "Retired(full-time)", value: "Retired(full-time)" },
  { name: "Roofer", value: "Roofer" },
  { name: "Sales Agent/Representative", value: "Sales Agent/Representative" },
  { name: "Sales Representative", value: "Sales Representative" },
  { name: "Sales-Homebased", value: "Sales-Homebased" },
  { name: "Sales-Retail/Wholesale", value: "Sales-Retail/Wholesale" },
  { name: "Student(full-time)", value: "Student(full-time)" },
  { name: "Support Services", value: "Support Services" },
  { name: "Teacher College", value: "Teacher College" },
  { name: "Teacher K-12", value: "Teacher K-12" },
  { name: "Underwriter", value: "Underwriter" },
  { name: "Unemployed", value: "Unemployed" },
  { name: "US Postal Worker", value: "US Postal Worker" },
  { name: "Waiter/Waitress", value: "Waiter/Waitress" },
  { name: "Web Developer", value: "Web Developer" },
  { name: "Welder", value: "Welder" },
];

interface DriverInformationProps {
  onNext?: (data?: any) => void;
  onPrevious?: () => void;
  workflowData?: any;
  currentStep?: number;
  totalSteps?: number;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  selectedState?: string;
  previousSelectedState?: string;
  insuredId?: string;
  loading?: boolean;
}

interface ViolationEntry {
  code: string;
  date: string;
  occurredOnSameDay: boolean;
}

interface DriverData {
  databaseId?: string;
  driverLicenseClass?: string;
  driverType: string;
  relationship: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  maritalStatus: string;
  education: string;
  occupation: string;
  yearsEmployed?: string;
  monthsEmployed?: string;
  milesToWork?: string;
  annualMiles?: string;
  dlNumber: string;
  dlState: string;
  dlYear: string;
  accidentsOrClaims: string;
  accidentViolationCode?: string;
  accidentViolationDate?: string;
  violations?: ViolationEntry[];
  sr22: string;
  sr22State: string;
  sr22Reason?: string;
  sr22Date?: string;

  countryOfOrigin?: string;
  licenseStatus?: string;
  internationalDriversLicense?: string;
  licenseState?: string;
}

function DriverContent({
  drivers,
  setDrivers,
  handleDriverInputChange,
  removeDriver,
  handleAddDriver,
  handleClearForm,
  handleViolationChange,
  addViolation,
  removeViolation,
  isFirstStep,
  onPrevious,
  onNext,
  driverLoading,
  validationErrors,
  validateForm,
  toast,
  loading,
  selectedState,
  policyType,
}: {
  drivers: Array<DriverData & { id: number; isExpanded: boolean }>;
  setDrivers: React.Dispatch<React.SetStateAction<Array<DriverData & { id: number; isExpanded: boolean }>>>;
  handleDriverInputChange: (id: number, e: React.ChangeEvent<any>) => void;
  removeDriver: (id: number) => void;
  handleAddDriver: () => void;
  handleClearForm: () => void;
  handleViolationChange: (driverId: number, violationIndex: number, field: "code" | "date" | "occurredOnSameDay", value: string | boolean) => void;
  addViolation: (driverId: number) => void;
  removeViolation: (driverId: number, violationIndex: number) => void;
  isFirstStep?: boolean;
  onPrevious?: () => void;
  onNext?: (data?: any) => void;
  driverLoading?: boolean;
  validationErrors?: Record<string, string>;
  validateForm?: () => boolean;
  toast?: React.RefObject<Toast | null>;
  loading?: boolean;
  selectedState?: string;
  policyType?: string;
}) {
  return (
    <div className="page-container" style={{ position: "relative" }}>
      <Toast ref={toast} />
      {/* Loading Overlay */}
      {driverLoading && (
        <div className="driver-loading-overlay">
          <div className="driver-loading-content">
            <div className="driver-loading-spinner" />
            <p className="driver-loading-text">Loading driver information...</p>
          </div>
        </div>
      )}
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-title-section">
            <h1>Driver Information</h1>
            <span className="title-separator">|</span>
            <p>
              Enter and manage driver details for the policy ({drivers.length} driver{drivers.length !== 1 ? "s" : ""})
            </p>
          </div>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          // Validate form before proceeding
          if (validateForm && !validateForm()) {
            return;
          }
          // Handle form submission and pass data to workflow
          if (onNext) {
            onNext(drivers);
          }
        }}
        className="vehicle-form"
      >
        {/* Compact select style for table look */}

        <div className="form-container">
          {/* Driver Summary DataTable */}
          {drivers.length > 0 && (
            <div className="m-2" style={{ marginBottom: "2rem" }}>
              <DataTable className="custom-datatable" value={drivers} showGridlines responsiveLayout="scroll" size="small" style={{ width: "100%" }}>
                <Column field="id" header="#" body={(rowData, options) => options.rowIndex + 1} style={{ width: "40px" }} />
                <Column field="firstName" header="First Name" />
                <Column field="lastName" header="Last Name" />
                <Column
                  field="dateOfBirth"
                  header="Date of Birth"
                  body={(rowData) => {
                    if (!rowData.dateOfBirth) return "";
                    const d = new Date(rowData.dateOfBirth);
                    if (isNaN(d.getTime())) return rowData.dateOfBirth;
                    const mm = String(d.getMonth() + 1).padStart(2, "0");
                    const dd = String(d.getDate()).padStart(2, "0");
                    const yyyy = d.getFullYear();
                    return `${mm}/${dd}/${yyyy}`;
                  }}
                />
                <Column field="gender" header="Gender" />
                <Column field="relationship" header="Relationship" />
                <Column field="driverType" header="Driver Type" />
                <Column
                  header="Actions"
                  body={(rowData) => (
                    <span>
                      <button
                        type="button"
                        title="Edit driver"
                        style={{ background: "none", border: "none", cursor: "pointer", marginRight: "8px" }}
                        onClick={() => {
                          setDrivers((prev) =>
                            prev.map((driver) => {
                              if (driver.id === rowData.id) {
                                // Only expand if not already expanded
                                return { ...driver, isExpanded: true };
                              }
                              return { ...driver, isExpanded: false };
                            }),
                          );
                          setTimeout(() => {
                            const card = document.querySelector(`[data-driver-id="${rowData.id}"]`);
                            if (card) card.scrollIntoView({ behavior: "smooth", block: "start" });
                          }, 100);
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M4 21v-3.5a2 2 0 0 1 .59-1.41l10.83-10.83a2 2 0 0 1 2.83 0l1.91 1.91a2 2 0 0 1 0 2.83L9.33 20.41A2 2 0 0 1 7.92 21H4z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      {drivers.length > 1 && (
                        <button
                          type="button"
                          title="Remove driver"
                          style={{ background: "none", border: "none", cursor: "pointer" }}
                          onClick={() => removeDriver(rowData.id)}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" />
                          </svg>
                        </button>
                      )}
                    </span>
                  )}
                  style={{ width: "90px", textAlign: "center" }}
                />
              </DataTable>
            </div>
          )}
          {/* Render each driver as a collapsible card */}
          {drivers.map((driver, index) => {
            return (
              <div key={driver.id} className="driver-card-section" data-driver-id={driver.id}>
                <div
                  className="driver-card-header"
                  onClick={() => {
                    setDrivers((prev) =>
                      prev.map((d) => {
                        if (d.id === driver.id) {
                          // Only expand if not already expanded
                          return { ...d, isExpanded: !d.isExpanded };
                        }
                        return { ...d, isExpanded: false };
                      }),
                    );
                  }}
                >
                  <div className="driver-card-title">
                    <svg viewBox="0 0 24 24" fill="none" style={{ width: "20px", height: "20px" }}>
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" />
                      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    <h3>
                      Driver #{index + 1}
                      {driver.firstName && driver.lastName && ` - ${driver.firstName} ${driver.lastName}`}
                    </h3>
                  </div>
                  <div className="driver-card-actions" onClick={(e) => e.stopPropagation()}>
                    {drivers.length > 1 && (
                      <button
                        type="button"
                        className="btn-remove-driver"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeDriver(driver.id);
                        }}
                        title="Remove driver"
                      >
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" />
                        </svg>
                      </button>
                    )}
                    <button
                      type="button"
                      className={`btn-toggle-driver ${driver.isExpanded ? "expanded" : ""}`}
                      onClick={() => {
                        setDrivers((prev) =>
                          prev.map((d) => {
                            if (d.id === driver.id) {
                              // Only expand if not already expanded
                              return { ...d, isExpanded: !d.isExpanded };
                            }
                            return { ...d, isExpanded: false };
                          }),
                        );
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M19 9l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div id={`personal-info-${driver.id}`} className={`driver-card-content ${driver.isExpanded ? "expanded" : "collapsed"}`}>
                  {/* Personal Information */}
                  <div className="form-section-header">
                    <h3>Personal Information</h3>
                  </div>

                  <div className="form-grid">
                    <div className="form-group">
                      <label>
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        className={`form-input ${validationErrors?.[`driver${driver.id}.firstName`] ? "error" : ""}`}
                        placeholder="Enter first name"
                        value={driver.firstName}
                        onChange={(e) => handleDriverInputChange(driver.id, e)}
                      />
                      {validationErrors?.[`driver${driver.id}.firstName`] && (
                        <span className="error-message">{validationErrors[`driver${driver.id}.firstName`]}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        className={`form-input ${validationErrors?.[`driver${driver.id}.lastName`] ? "error" : ""}`}
                        placeholder="Enter last name"
                        value={driver.lastName}
                        onChange={(e) => handleDriverInputChange(driver.id, e)}
                      />
                      {validationErrors?.[`driver${driver.id}.lastName`] && (
                        <span className="error-message">{validationErrors[`driver${driver.id}.lastName`]}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>
                        Date of Birth <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="dateOfBirth"
                        placeholder="MM/DD/YYYY"
                        className={`form-input ${validationErrors?.[`driver${driver.id}.dateOfBirth`] ? "error" : ""}`}
                        value={/^\d{4}-\d{2}-\d{2}$/.test(driver.dateOfBirth) ? formatDate(driver.dateOfBirth) : driver.dateOfBirth}
                        onChange={(e) => {
                          const formatted = formatDateOfBirth(e.target.value);
                          handleDriverInputChange(driver.id, {
                            target: { name: "dateOfBirth", value: formatted },
                          } as React.ChangeEvent<HTMLInputElement>);
                        }}
                        autoComplete="off"
                      />
                      {validationErrors?.[`driver${driver.id}.dateOfBirth`] && (
                        <span className="error-message">{validationErrors[`driver${driver.id}.dateOfBirth`]}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>
                        Gender <span className="text-danger">*</span>
                      </label>
                      <select
                        name="gender"
                        className={`form-input compact-select ${validationErrors?.[`driver${driver.id}.gender`] ? "error" : ""}`}
                        value={driver.gender}
                        onChange={(e) => handleDriverInputChange(driver.id, e)}
                      >
                        <option value="">Select gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Non-Binary">Non-Binary</option>
                      </select>
                      {validationErrors?.[`driver${driver.id}.gender`] && (
                        <span className="error-message">{validationErrors[`driver${driver.id}.gender`]}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>
                        Marital Status <span className="text-danger">*</span>
                      </label>
                      <select
                        name="maritalStatus"
                        className={`form-input compact-select ${validationErrors?.[`driver${driver.id}.maritalStatus`] ? "error" : ""}`}
                        value={driver.maritalStatus}
                        onChange={(e) => handleDriverInputChange(driver.id, e)}
                      >
                        <option value="">Select marital status</option>
                        <option value="Single">Single</option>
                        <option value="Married">Married</option>
                        <option value="Divorced">Divorced</option>
                        <option value="Widowed">Widowed</option>
                        <option value="Separated">Separated</option>
                        <option value="Domestic Partner">Domestic Partner</option>
                        <option value="Common Law">Common Law</option>
                      </select>
                      {validationErrors?.[`driver${driver.id}.maritalStatus`] && (
                        <span className="error-message">{validationErrors[`driver${driver.id}.maritalStatus`]}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Education Level</label>
                      <select
                        name="education"
                        className={`form-input compact-select ${validationErrors?.[`driver${driver.id}.education`] ? "error" : ""}`}
                        value={driver.education}
                        onChange={(e) => handleDriverInputChange(driver.id, e)}
                      >
                        <option value="">Select education level</option>
                        {EDUCATION_OPTIONS_UNIFIED.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {validationErrors?.[`driver${driver.id}.education`] && (
                        <span className="error-message">{validationErrors[`driver${driver.id}.education`]}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Occupation</label>
                      <select
                        name="occupation"
                        className={`form-input compact-select ${validationErrors?.[`driver${driver.id}.occupation`] ? "error" : ""}`}
                        value={driver.occupation}
                        onChange={(e) => handleDriverInputChange(driver.id, e)}
                      >
                        <option value="">Select occupation</option>
                        {occupationOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                      {validationErrors?.[`driver${driver.id}.occupation`] && (
                        <span className="error-message">{validationErrors[`driver${driver.id}.occupation`]}</span>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Years Employed</label>
                      <input
                        type="number"
                        name="yearsEmployed"
                        className="form-input"
                        placeholder="Enter years employed"
                        value={driver.yearsEmployed || ""}
                        onChange={(e) => handleDriverInputChange(driver.id, e)}
                        min={0}
                        max={99}
                        step={1}
                        onKeyDown={(e) => [".", "e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                      />
                    </div>
                    <div className="form-group">
                      <label>Months Employed</label>
                      <input
                        type="number"
                        name="monthsEmployed"
                        className="form-input"
                        placeholder="Enter months employed"
                        value={driver.monthsEmployed || ""}
                        onChange={(e) => handleDriverInputChange(driver.id, e)}
                        min="0"
                        max="11"
                      />
                    </div>
                    {policyType === "Nonowner" && (
                      <>
                        <div className="form-group">
                          <label>
                            Miles to Work <span className="text-danger">*</span>
                          </label>
                          <input
                            type="number"
                            name="milesToWork"
                            className={`form-input ${validationErrors?.[`driver${driver.id}.milesToWork`] ? "error" : ""}`}
                            placeholder="Enter miles to work"
                            value={driver.milesToWork || ""}
                            onChange={(e) => handleDriverInputChange(driver.id, e)}
                            min="0"
                            max="999"
                          />
                          {validationErrors?.[`driver${driver.id}.milesToWork`] && (
                            <span className="error-message">{validationErrors[`driver${driver.id}.milesToWork`]}</span>
                          )}
                        </div>
                        <div className="form-group">
                          <label>
                            Annual Miles <span className="text-danger">*</span>
                          </label>
                          <input
                            type="number"
                            name="annualMiles"
                            className={`form-input ${validationErrors?.[`driver${driver.id}.annualMiles`] ? "error" : ""}`}
                            placeholder="Enter annual miles"
                            value={driver.annualMiles || ""}
                            onChange={(e) => handleDriverInputChange(driver.id, e)}
                            min="0"
                            max="1000000"
                          />
                          {validationErrors?.[`driver${driver.id}.annualMiles`] && (
                            <span className="error-message">{validationErrors[`driver${driver.id}.annualMiles`]}</span>
                          )}
                        </div>
                      </>
                    )}
                    <div className="form-group">
                      <label>
                        Relationship <span className="text-danger">*</span>
                      </label>
                      {driver.id === 1 ? (
                        <select name="relationship" className="form-input compact-select" value="Insured" disabled>
                          <option value="Insured">Insured</option>
                        </select>
                      ) : (
                        <>
                          <select
                            name="relationship"
                            className={`form-input compact-select ${validationErrors?.[`driver${driver.id}.relationship`] ? "error" : ""}`}
                            value={driver.relationship}
                            onChange={(e) => handleDriverInputChange(driver.id, e)}
                          >
                            <option value="">Select relationship</option>
                            <option value="Insured">Insured</option>
                            <option value="Spouse">Spouse</option>
                            <option value="Child">Child</option>
                            <option value="Other Related">Other Related</option>
                            <option value="Other Non Related">Other Non Related</option>
                            <option value="Parent">Parent</option>
                          </select>
                          {validationErrors?.[`driver${driver.id}.relationship`] && (
                            <span className="error-message">{validationErrors[`driver${driver.id}.relationship`]}</span>
                          )}
                        </>
                      )}
                    </div>
                    {/* Driver Type Field */}
                    <div className="form-group">
                      <label>
                        Driver Type <span className="text-danger">*</span>
                      </label>
                      <select
                        name="driverType"
                        className={`form-input compact-select ${validationErrors?.[`driver${driver.id}.driverType`] ? "error" : ""}`}
                        value={driver.driverType || (driver.id === 1 ? "Rated" : "")}
                        onChange={(e) => {
                          if (drivers.length === 1) return;
                          handleDriverInputChange(driver.id, e);
                        }}
                        disabled={driver.id === 1 || drivers.length === 1}
                      >
                        <option value="Rated">Rated</option>
                        <option value="Excluded">Excluded</option>
                      </select>
                      {validationErrors?.[`driver${driver.id}.driverType`] && (
                        <span className="error-message">{validationErrors[`driver${driver.id}.driverType`]}</span>
                      )}
                    </div>
                  </div>

                  {/* License Information */}
                  <div className="form-section-header" id={`license-info-${driver.id}`}>
                    <h3>License Information</h3>
                  </div>

                  <div className="form-grid">
                    {/* Always show these 3 fields */}
                    <div className="form-group">
                      <label>Driver's License Number </label>
                      <input
                        type="text"
                        name="dlNumber"
                        className="form-input"
                        placeholder="Enter license number"
                        value={driver.dlNumber}
                        onChange={(e) => handleDriverInputChange(driver.id, e)}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        License State <span className="text-danger">*</span>
                      </label>
                      <select
                        name="dlState"
                        className={`form-input compact-select ${validationErrors?.[`driver${driver.id}.dlState`] ? "error" : ""}`}
                        value={driver.dlState}
                        onChange={(e) => handleDriverInputChange(driver.id, e)}
                      >
                        <option value="">Select state</option>
                        <option value="Alabama">Alabama</option>
                        <option value="Alaska">Alaska</option>
                        <option value="Arizona">Arizona</option>
                        <option value="Arkansas">Arkansas</option>
                        <option value="California">California</option>
                        <option value="Colorado">Colorado</option>
                        <option value="Connecticut">Connecticut</option>
                        <option value="Delaware">Delaware</option>
                        <option value="Florida">Florida</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Hawaii">Hawaii</option>
                        <option value="Idaho">Idaho</option>
                        <option value="Illinois">Illinois</option>
                        <option value="Indiana">Indiana</option>
                        <option value="Iowa">Iowa</option>
                        <option value="Kansas">Kansas</option>
                        <option value="Kentucky">Kentucky</option>
                        <option value="Louisiana">Louisiana</option>
                        <option value="Maine">Maine</option>
                        <option value="Maryland">Maryland</option>
                        <option value="Massachusetts">Massachusetts</option>
                        <option value="Michigan">Michigan</option>
                        <option value="Minnesota">Minnesota</option>
                        <option value="Mississippi">Mississippi</option>
                        <option value="Missouri">Missouri</option>
                        <option value="Montana">Montana</option>
                        <option value="Nebraska">Nebraska</option>
                        <option value="Nevada">Nevada</option>
                        <option value="New Hampshire">New Hampshire</option>
                        <option value="New Jersey">New Jersey</option>
                        <option value="New Mexico">New Mexico</option>
                        <option value="New York">New York</option>
                        <option value="North Carolina">North Carolina</option>
                        <option value="North Dakota">North Dakota</option>
                        <option value="Ohio">Ohio</option>
                        <option value="Oklahoma">Oklahoma</option>
                        <option value="Oregon">Oregon</option>
                        <option value="Pennsylvania">Pennsylvania</option>
                        <option value="Rhode Island">Rhode Island</option>
                        <option value="South Carolina">South Carolina</option>
                        <option value="South Dakota">South Dakota</option>
                        <option value="Tennessee">Tennessee</option>
                        <option value="Texas">Texas</option>
                        <option value="Utah">Utah</option>
                        <option value="Vermont">Vermont</option>
                        <option value="Virginia">Virginia</option>
                        <option value="Washington">Washington</option>
                        <option value="West Virginia">West Virginia</option>
                        <option value="Wisconsin">Wisconsin</option>
                        <option value="Wyoming">Wyoming</option>
                      </select>
                      {validationErrors?.[`driver${driver.id}.dlState`] && (
                        <span className="error-message">{validationErrors[`driver${driver.id}.dlState`]}</span>
                      )}
                    </div>
                    <div className="form-group">
                      <label>
                        Year Licensed <span className="text-danger">*</span>
                      </label>
                      <select
                        name="dlYear"
                        className={`form-input compact-select ${validationErrors?.[`driver${driver.id}.dlYear`] ? "error" : ""}`}
                        value={driver.dlYear}
                        onChange={(e) => handleDriverInputChange(driver.id, e)}
                      >
                        <option value="">Select year first licensed</option>
                        {Array.from({ length: 50 }, (_, i) => 2024 - i).map((year) => (
                          <option key={year} value={year.toString()}>
                            {year}
                          </option>
                        ))}
                      </select>
                      {validationErrors?.[`driver${driver.id}.dlYear`] && (
                        <span className="error-message">{validationErrors[`driver${driver.id}.dlYear`]}</span>
                      )}
                    </div>

                    {/* Conditional extra fields by state */}
                    {/* Ohio: LicenseStatus, InternationalDriversLicense, CountryOfOrigin */}

                    <LicenseStatusField
                      value={driver.licenseStatus || ""}
                      onChange={(e: any) => handleDriverInputChange(driver.id, e)}
                      selectedState={selectedState}
                      error={validationErrors?.[`driver${driver.id}.licenseStatus`] || ""}
                    />

                    <InternationalDriversLicenseField
                      value={driver.internationalDriversLicense || ""}
                      onChange={(e: any) => handleDriverInputChange(driver.id, e)}
                    />
                    <CountryOfOriginField
                      value={driver.countryOfOrigin || ""}
                      onChange={(e: any) => handleDriverInputChange(driver.id, e)}
                      required={driver.internationalDriversLicense === "Yes"}
                      error={validationErrors?.[`driver${driver.id}.countryOfOrigin`] || ""}
                    />
                  </div>

                  {/* Driving History & Background */}
                  <div className="form-section-header" id={`driving-history-${driver.id}`}>
                    <h3>Driving History & Background</h3>
                  </div>

                  <div className="form-grid">
                    <div className="form-group">
                      <label>
                        Any Accidents or Violations in the Previous 3 Years <span className="text-danger">*</span>
                      </label>
                      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <select
                          name="accidentsOrClaims"
                          className={`form-input compact-select ${validationErrors?.[`driver${driver.id}.accidentsOrClaims`] ? "error" : ""}`}
                          value={driver.accidentsOrClaims}
                          onChange={(e) => handleDriverInputChange(driver.id, e)}
                          style={{ flex: 1 }}
                        >
                          <option value="">Select option</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                        {driver.accidentsOrClaims === "Yes" && (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ whiteSpace: "nowrap", padding: "4px 10px", fontSize: "12px", flexShrink: 0 }}
                            onClick={() => addViolation(driver.id)}
                          >
                            + Add Violation
                          </button>
                        )}
                      </div>
                      {validationErrors?.[`driver${driver.id}.accidentsOrClaims`] && (
                        <span className="error-message">{validationErrors[`driver${driver.id}.accidentsOrClaims`]}</span>
                      )}
                    </div>

                    {driver.accidentsOrClaims === "Yes" && (
                      <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                        {(driver.violations || []).map((violation, vIndex) => (
                          <div
                            key={vIndex}
                            style={{
                              display: "flex",
                              gap: "12px",
                              alignItems: "flex-start",
                              marginBottom: "12px",
                              padding: "12px",
                              border: "1px solid #e0e0e0",
                              borderRadius: "6px",
                              // background: "#fafafa",
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <label>
                                Accident/Violation Code <span className="text-danger">*</span>
                              </label>
                              <select
                                className={`form-input compact-select ${validationErrors?.[`driver${driver.id}.violations[${vIndex}].code`] ? "error" : ""}`}
                                value={violation.code}
                                onChange={(e) => handleViolationChange(driver.id, vIndex, "code", e.target.value)}
                              >
                                <option value="">Select code</option>
                                <option value="Accident, At-Fault">Accident, At-Fault</option>
                                <option value="Accident, Not At-Fault">Accident, Not At-Fault</option>
                                <option value="All Other Moving Violations">All Other Moving Violations</option>
                                <option value="All Other Non-Moving Violations">All Other Non-Moving Violations</option>
                                <option value="Comprehensive Claim">Comprehensive Claim</option>
                                <option value="Driving While Suspended or Revoked">Driving While Suspended or Revoked</option>
                                <option value="Driving With an Expired License">Driving With an Expired License</option>
                                <option value="Driving Without a License or Permit">Driving Without a License or Permit</option>
                                <option value="Driving Wrong Way on One-Way">Driving Wrong Way on One-Way</option>
                                <option value="DUI Alcohol/Liquor">DUI Alcohol/Liquor</option>
                                <option value="DWI">DWI</option>
                                <option value="Eluding Police/Evading Arrest">Eluding Police/Evading Arrest</option>
                                <option value="Failure to Stop for Red Light">Failure to Stop for Red Light</option>
                                <option value="Failure to Stop for Stop Sign">Failure to Stop for Stop Sign</option>
                                <option value="Failure to Wear Seat Belt">Failure to Wear Seat Belt</option>
                                <option value="Failure to Yield Right of Way">Failure to Yield Right of Way</option>
                                <option value="Improper Passing of a School Bus">Improper Passing of a School Bus</option>
                                <option value="Leaving Scene / Hit-and-Run">Leaving Scene / Hit-and-Run</option>
                                <option value="Medical Payments Claim">Medical Payments Claim</option>
                                <option value="No Liability Insurance in Force">No Liability Insurance in Force</option>
                                <option value="Open Container Violation">Open Container Violation</option>
                                <option value="PIP Claim">PIP Claim</option>
                                <option value="Racing/Speed Contest">Racing/Speed Contest</option>
                                <option value="Reckless Driving">Reckless Driving</option>
                                <option value="Speeding">Speeding</option>
                                <option value="Speeding in a School Zone">Speeding in a School Zone</option>
                                <option value="Underinsured Motorist Claim">Underinsured Motorist Claim</option>
                                <option value="Uninsured Motorist Claim">Uninsured Motorist Claim</option>
                                <option value="Use of Wireless Device without Handsfree while Driving">
                                  Use of Wireless Device without Handsfree while Driving
                                </option>
                                <option value="Violating Safety Zone">Violating Safety Zone</option>
                              </select>
                              {validationErrors?.[`driver${driver.id}.violations[${vIndex}].code`] && (
                                <span className="error-message">{validationErrors[`driver${driver.id}.violations[${vIndex}].code`]}</span>
                              )}
                            </div>

                            <div style={{ flex: 1 }}>
                              <label>
                                Accident/Violation Date <span className="text-danger">*</span>
                              </label>
                              <DatePicker
                                className={`form-input ${validationErrors?.[`driver${driver.id}.violations[${vIndex}].date`] ? "error" : ""}`}
                                placeholderText="MM/DD/YYYY"
                                selected={violation.date ? new Date(violation.date) : null}
                                onChange={(date: Date | null) => {
                                  handleViolationChange(driver.id, vIndex, "date", date ? formatDate(date) : "");
                                }}
                                dateFormat="MM/dd/yyyy"
                                showYearDropdown
                                showMonthDropdown
                                dropdownMode="select"
                                maxDate={new Date()}
                              />
                              {validationErrors?.[`driver${driver.id}.violations[${vIndex}].date`] && (
                                <span className="error-message">{validationErrors[`driver${driver.id}.violations[${vIndex}].date`]}</span>
                              )}
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: "80px", marginTop: "4px" }}>
                              <label style={{ fontSize: "11px", textAlign: "center", marginBottom: "4px" }}>Same Day</label>
                              <InputSwitch
                                checked={violation.occurredOnSameDay}
                                onChange={(e) => handleViolationChange(driver.id, vIndex, "occurredOnSameDay", e.value)}
                              />
                            </div>

                            {(driver.violations || []).length > 1 && (
                              <button
                                type="button"
                                title="Remove violation"
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  marginTop: "28px",
                                  color: "#dc3545",
                                  padding: "4px",
                                }}
                                onClick={() => removeViolation(driver.id, vIndex)}
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" />
                                </svg>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* SR22 Fields */}
                  <div className="form-section-header" id={`sr22-info-${driver.id}`}>
                    <h3>SR22 Information</h3>
                  </div>
                  <div className="grid grid-cols-4 gap-2 p-6">
                    <div className="form-group w-full">
                      <label htmlFor={`sr22-switch-${driver.id}`}>SR22</label>
                      <InputSwitch
                        id={`sr22-switch-${driver.id}`}
                        checked={driver.sr22 === "Yes"}
                        onChange={(e) => setDrivers((prev) => prev.map((d) => (d.id === driver.id ? { ...d, sr22: e.value ? "Yes" : "No" } : d)))}
                      />
                    </div>
                    <div className="form-group w-full">
                      <label>SR22 State</label>
                      <select
                        name="sr22State"
                        className="form-input compact-select"
                        value={driver.sr22State}
                        onChange={(e) => handleDriverInputChange(driver.id, e)}
                      >
                        <option value="">Select state</option>
                        <option value="Alabama">Alabama</option>
                        <option value="Alaska">Alaska</option>
                        <option value="Arizona">Arizona</option>
                        <option value="Arkansas">Arkansas</option>
                        <option value="California">California</option>
                        <option value="Colorado">Colorado</option>
                        <option value="Connecticut">Connecticut</option>
                        <option value="Delaware">Delaware</option>
                        <option value="Florida">Florida</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Hawaii">Hawaii</option>
                        <option value="Idaho">Idaho</option>
                        <option value="Illinois">Illinois</option>
                        <option value="Indiana">Indiana</option>
                        <option value="Iowa">Iowa</option>
                        <option value="Kansas">Kansas</option>
                        <option value="Kentucky">Kentucky</option>
                        <option value="Louisiana">Louisiana</option>
                        <option value="Maine">Maine</option>
                        <option value="Maryland">Maryland</option>
                        <option value="Massachusetts">Massachusetts</option>
                        <option value="Michigan">Michigan</option>
                        <option value="Minnesota">Minnesota</option>
                        <option value="Mississippi">Mississippi</option>
                        <option value="Missouri">Missouri</option>
                        <option value="Montana">Montana</option>
                        <option value="Nebraska">Nebraska</option>
                        <option value="Nevada">Nevada</option>
                        <option value="New Hampshire">New Hampshire</option>
                        <option value="New Jersey">New Jersey</option>
                        <option value="New Mexico">New Mexico</option>
                        <option value="New York">New York</option>
                        <option value="North Carolina">North Carolina</option>
                        <option value="North Dakota">North Dakota</option>
                        <option value="Ohio">Ohio</option>
                        <option value="Oklahoma">Oklahoma</option>
                        <option value="Oregon">Oregon</option>
                        <option value="Pennsylvania">Pennsylvania</option>
                        <option value="Rhode Island">Rhode Island</option>
                        <option value="South Carolina">South Carolina</option>
                        <option value="South Dakota">South Dakota</option>
                        <option value="Tennessee">Tennessee</option>
                        <option value="Texas">Texas</option>
                        <option value="Utah">Utah</option>
                        <option value="Vermont">Vermont</option>
                        <option value="Virginia">Virginia</option>
                        <option value="Washington">Washington</option>
                        <option value="West Virginia">West Virginia</option>
                        <option value="Wisconsin">Wisconsin</option>
                        <option value="Wyoming">Wyoming</option>
                      </select>
                    </div>

                    {driver.sr22 === "Yes" && (
                      <>
                        <div className="form-group w-full">
                          <label>
                            SR22 Reason <span className="text-danger">*</span>
                          </label>
                          <select
                            name="sr22Reason"
                            className={`form-input compact-select ${validationErrors?.[`driver${driver.id}.sr22Reason`] ? "error" : ""}`}
                            value={driver.sr22Reason || ""}
                            onChange={(e) => handleDriverInputChange(driver.id, e)}
                          >
                            <option value="">Select reason</option>
                            <option value="No Insurance">No Insurance</option>
                            <option value="Accident With Death">Accident With Death</option>
                            <option value="Accident No Insurance">Accident No Insurance</option>
                            <option value="Multiple Reckless Major Related">Multiple Reckless Major Related</option>
                            <option value="Alcohol Drug Related">Alcohol Drug Related</option>
                            <option value="Mandatory Insurance Supervision">Mandatory Insurance Supervision</option>
                            <option value="Emancipated Minor">Emancipated Minor</option>
                            <option value="Other">Other</option>
                          </select>
                          {validationErrors?.[`driver${driver.id}.sr22Reason`] && (
                            <span className="error-message">{validationErrors[`driver${driver.id}.sr22Reason`]}</span>
                          )}
                        </div>

                        <div className="form-group w-full">
                          <label>
                            SR22 Date <span className="text-danger">*</span>
                          </label>
                          <DatePicker
                            name="sr22Date"
                            className={`form-input ${validationErrors?.[`driver${driver.id}.sr22Date`] ? "error" : ""}`}
                            placeholderText="MM/DD/YYYY"
                            selected={driver.sr22Date ? new Date(driver.sr22Date) : null}
                            onChange={(date: Date | null) => {
                              handleDriverInputChange(driver.id, {
                                target: {
                                  name: "sr22Date",
                                  value: date ? formatDate(date) : "",
                                },
                              } as React.ChangeEvent<HTMLInputElement>);
                            }}
                            dateFormat="MM/dd/yyyy"
                            showYearDropdown
                            showMonthDropdown
                            dropdownMode="select"
                          />
                          {validationErrors?.[`driver${driver.id}.sr22Date`] && (
                            <span className="error-message">{validationErrors[`driver${driver.id}.sr22Date`]}</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Add Driver Section */}
          <div className="form-section-header">
            <h3>Additional Options</h3>
          </div>

          <div className="add-coverage-section" style={{ marginBottom: "2rem" }}>
            <button type="button" className="btn btn-secondary" onClick={handleAddDriver}>
              {/* <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                style={{ width: "16px", height: "16px" }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg> */}
              Add Driver
            </button>
          </div>

          {!driverLoading && (
            <div className="form-actions">
              {!isFirstStep && (
                <button className="btn btn-secondary" onClick={onPrevious}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" stroke="currentColor" />
                  </svg>
                  Previous
                </button>
              )}
              {/* <button className="btn btn-secondary" onClick={handleClearForm}>
              
                Clear Form
              </button> */}
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  "Saving..."
                ) : (
                  <>
                    Continue
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M9 5l7 7-7 7" stroke="currentColor" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

export default function DriverInformation({
  onNext,
  onPrevious,
  workflowData,
  currentStep,
  totalSteps,
  isFirstStep,
  isLastStep,
  selectedState,
  previousSelectedState,
  insuredId,
  loading,
}: DriverInformationProps = {}) {
  const toast = React.useRef<Toast>(null);
  const [driverConfigurationList, setDriverConfigurationList] = useState<any>([]);
  const [driverLoading, setDriverLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Add Driver handler (restored)
  const handleAddDriver = () => {
    const newId = nextDriverId;
    const newDriver = {
      id: newId,
      isExpanded: true,
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      gender: "",
      maritalStatus: "",
      education: driverConfigurationList?.education || "",
      occupation: driverConfigurationList?.occupation || "",
      dlNumber: "",
      dlState: driverConfigurationList?.licenseState || "",
      dlYear: "",
      accidentsOrClaims: driverConfigurationList.previousClaims,
      accidentViolationCode: "",
      accidentViolationDate: "",
      violations: driverConfigurationList.previousClaims === "Yes" ? [{ code: "", date: "", occurredOnSameDay: false }] : [],
      relationship: driverConfigurationList?.relationship || "",
      driverType: driverConfigurationList?.driverType || "",
      sr22: driverConfigurationList?.sR22 || "",
      sr22State: driverConfigurationList?.sR22State || "",
      sr22Reason: "",
      sr22Date: "",
      licenseState: driverConfigurationList?.licenseState || "",
      milesToWork: driverConfigurationList?.milesToWork || "",
      annualMiles: driverConfigurationList?.annualMiles || "",
      yearsEmployed: driverConfigurationList?.yearsEmployed || "",
      monthsEmployed: driverConfigurationList?.monthsEmployed || "",
      licenseStatus: driverConfigurationList?.licenseStatus || "",
      countryOfOrigin: driverConfigurationList?.countryOfOrigin || "",
      internationalDriversLicense: driverConfigurationList?.internationalDriversLicense || "",
    };
    setDrivers((prev) => [...prev.map((d) => ({ ...d, isExpanded: false })), newDriver]);
    setNextDriverId((prevId) => prevId + 1);
    setTimeout(() => {
      const newDriverCard = document.querySelector(`[data-driver-id="${newId}"]`);
      if (newDriverCard) {
        newDriverCard.scrollIntoView({ behavior: "auto", block: "start" });
        // Focus the first input (first name)
        const firstInput = newDriverCard.querySelector('input[name="firstName"]');
        if (firstInput) {
          (firstInput as HTMLInputElement).focus();
        }
      }
    }, 100);
  };

  const navigate = useNavigate();
  const location = useLocation();

  // State for multiple drivers
  const [drivers, setDrivers] = useState<Array<DriverData & { id: number; isExpanded: boolean }>>(() => {
    return [];
  });

  const [nextDriverId, setNextDriverId] = useState(2);

  const [driverData, setDriverData] = useState<DriverData>({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    maritalStatus: "",
    education: "",
    occupation: "",
    dlNumber: "",
    dlState: "",
    dlYear: "",
    accidentsOrClaims: "",
    accidentViolationCode: "",
    accidentViolationDate: "",
    relationship: "",
    driverType: "",
    sr22: "",
    sr22State: "",
    sr22Reason: "",
    sr22Date: "",
  });
  useEffect(() => {
    if (!workflowData) return;

    /** CASE 1: Step2 exists → use it */
    if (workflowData.step2 && workflowData.step2.length > 0) {
      setDrivers(workflowData.step2);
      return;
    }

    /** CASE 2: Step2 empty → create Insured from Step1 */
    if (workflowData.step1) {
      setDrivers([
        {
          id: 1,
          isExpanded: true,
          driverType: "Rated",
          relationship: "Insured",

          firstName: workflowData.step1.firstName || "",
          lastName: workflowData.step1.lastName || "",
          dateOfBirth: workflowData.step1.dateOfBirth || "",

          gender: "",
          maritalStatus: "",
          education: "",
          occupation: "",

          dlNumber: "",
          dlState: "",
          dlYear: "",

          accidentsOrClaims: "",
          accidentViolationCode: "",
          accidentViolationDate: "",
          violations: [],
          sr22: "",
          sr22State: "",
          sr22Reason: "",
          sr22Date: "",
        },
      ]);
    }
  }, [workflowData]);

  useEffect(() => {
    // Scroll main layout-content container to top
    const layoutContent = document.querySelector(".layout-content");
    if (layoutContent) {
      layoutContent.scrollTo({ top: 0, behavior: "auto" });
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  // useEffect(() => {
  //   if (selectedState || workflowData?.step2.length === 0) {
  //     getDriverConfigurationDetails();
  //   }
  // }, [selectedState]);
  const previousStateFromWorkflow = workflowData?.step1?.previousSelectedState || previousSelectedState;

  // useEffect(() => {
  //   const stateChanged = previousStateFromWorkflow && previousStateFromWorkflow !== selectedState;
  //   const shouldCallConfig = selectedState && (!workflowData?.step2 || workflowData?.step2?.length === 0 || stateChanged);

  //   if (shouldCallConfig) {
  //     getDriverConfigurationDetails();
  //   }
  // }, [selectedState, previousStateFromWorkflow, workflowData]);
  useEffect(() => {
    if (selectedState) {
      getDriverConfigurationDetails();
    }
  }, [selectedState]);
  useEffect(() => {
    if (
      insuredId &&
      (!workflowData?.step2 || workflowData?.step2?.length === 0) &&
      driverConfigurationList &&
      Object.keys(driverConfigurationList).length > 0
    ) {
      getDriversListById();
    }
  }, [insuredId, driverConfigurationList]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Get policy type from workflowData
    const policyType = workflowData?.step1?.policyType;

    // Validate each driver
    drivers.forEach((driver, index) => {
      const driverPrefix = `driver${driver.id}`;

      // Personal Information
      if (!driver.firstName?.trim()) {
        errors[`${driverPrefix}.firstName`] = `First name is required`;
      }
      if (!driver.lastName?.trim()) {
        errors[`${driverPrefix}.lastName`] = `Last name is required`;
      }
      if (!driver.dateOfBirth) {
        errors[`${driverPrefix}.dateOfBirth`] = `Date of birth is required`;
      }
      if (!driver.gender) {
        errors[`${driverPrefix}.gender`] = `Gender is required`;
      }
      if (!driver.maritalStatus) {
        errors[`${driverPrefix}.maritalStatus`] = `Marital status is required`;
      }
      // if (!driver.education) {
      //   errors[`${driverPrefix}.education`] = `Education level is required`;
      // }
      // License State required
      if (!driver.dlState) {
        errors[`${driverPrefix}.dlState`] = `License state is required`;
      }
      // License Status required
      if (!driver.licenseStatus) {
        errors[`${driverPrefix}.licenseStatus`] = `License status is required`;
      }

      // if (!driver.occupation?.trim()) {
      //   errors[`${driverPrefix}.occupation`] = `Occupation is required`;
      // }
      if (!driver.relationship) {
        errors[`${driverPrefix}.relationship`] = `Relationship is required`;
      }
      if (!driver.driverType) {
        errors[`${driverPrefix}.driverType`] = `Driver type is required`;
      }

      // Conditional validation for Non-owner policy
      if (policyType === "Nonowner") {
        if (!driver.milesToWork?.trim()) {
          errors[`${driverPrefix}.milesToWork`] = `Miles to work is required for Non-owner policy`;
        }
        if (!driver.annualMiles?.trim()) {
          errors[`${driverPrefix}.annualMiles`] = `Annual miles is required for Non-owner policy`;
        }
      }

      // Conditional validation for International Drivers License
      if (driver.internationalDriversLicense === "Yes") {
        if (!driver.countryOfOrigin) {
          errors[`${driverPrefix}.countryOfOrigin`] = `Country of Origin is required when International Driver's License is Yes`;
        }
      }

      // License Information
      if (!driver.dlYear) {
        errors[`${driverPrefix}.dlYear`] = `Year licensed is required`;
      }

      // Driving History
      if (!driver.accidentsOrClaims) {
        errors[`${driverPrefix}.accidentsOrClaims`] = `Accidents or claims information is required`;
      }

      // Conditional validation for Accident/Violation fields
      if (driver.accidentsOrClaims === "Yes") {
        const violations = driver.violations || [];
        if (violations.length === 0) {
          errors[`${driverPrefix}.violations`] = `At least one violation is required`;
        }
        violations.forEach((violation, vIndex) => {
          if (!violation.code) {
            errors[`${driverPrefix}.violations[${vIndex}].code`] = `Accident/Violation Code is required`;
          }
          if (!violation.date) {
            errors[`${driverPrefix}.violations[${vIndex}].date`] = `Accident/Violation Date is required`;
          }
        });
      }

      // Conditional validation for SR22 fields
      if (driver.sr22 === "Yes") {
        if (!driver.sr22Reason) {
          errors[`${driverPrefix}.sr22Reason`] = `SR22 Reason is required`;
        }
        if (!driver.sr22Date) {
          errors[`${driverPrefix}.sr22Date`] = `SR22 Date is required`;
        }
      }
    });

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please fill in all required fields for all drivers",
        life: 3000,
      });

      // Scroll to first error field
      const firstErrorKey = Object.keys(errors)[0];
      const firstErrorField = document.querySelector(`[name="${firstErrorKey.split(".")[1]}"]`);
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      return false;
    }

    return true;
  };

  const getDriverConfigurationDetails = () => {
    setDriverLoading(true);
    axios({
      method: "get",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/driverinformationconfiguration?state=${selectedState}  `,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "Application/json",
      },
    })
      .then((response: any) => {
        const config = response.data;
        setDriverConfigurationList(config);
        setDrivers((prev) => {
          // Only update if we have drivers loaded, otherwise wait for driver list API
          if (prev.length === 0) return prev;

          const updatedDrivers = prev.map((driver) => ({
            ...driver,
            databaseId: driver.databaseId || "",
            education: driver.education || config.education || "",
            occupation: driver.occupation || config.occupation || "",
            relationship: driver.id === 1 ? "Insured" : driver.relationship || config.relationship || "",
            driverType: driver.id === 1 ? "Rated" : driver.driverType || config.driverType || "",
            dlState: driver.dlState || config.licenseState || "",
            sr22: driver.sr22 || config.sR22 || "",
            sr22State: driver.sr22State || config.sR22State || "",
            accidentsOrClaims: driver.accidentsOrClaims || config.previousClaims,
            violations: (driver.accidentsOrClaims || config.previousClaims) === "Yes" && (!driver.violations || driver.violations.length === 0)
              ? [{ code: "", date: "", occurredOnSameDay: false }]
              : driver.violations || [],
            milesToWork: driver.milesToWork || config.milesToWork || "",
            annualMiles: driver.annualMiles || config.annualMiles || "",
            yearsEmployed: driver.yearsEmployed || config.yearsEmployed || "",
            monthsEmployed: driver.monthsEmployed || config.monthsEmployed || "",
            licenseStatus: driver.licenseStatus || config.licenseStatus || "",
            licenseState: driver.licenseState || config.licenseState || "",
            countryOfOrigin: driver.countryOfOrigin || config.countryOfOrigin || "",
            internationalDriversLicense: driver.internationalDriversLicense || config.internationalDriversLicense || "",
          }));

          return updatedDrivers;
        });

        setDriverLoading(false);
      })
      .catch((error: any) => {
        setDriverLoading(false);
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response && error.response.status
                ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while searching the client!"}`
                : "Unknown error while getting  the driver configuration details!",
            life: 3000,
          });
        }
      });
  };
  const getDriversListById = () => {
    setDriverLoading(true);
    axios({
      method: "get",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/driverlist?insuredId=${insuredId}&ncToken=${localStorage.getItem("NCToken")}  `,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "Application/json",
      },
    })
      .then((response: any) => {
        const driverList = response.data?.value || [];

        // If API has no drivers, apply configuration data to first driver from step1
        if (driverList.length === 0) {
          if (driverConfigurationList && Object.keys(driverConfigurationList).length > 0) {
            setDrivers((prev) => {
              if (prev.length === 0) return prev;

              return prev.map((driver) => ({
                ...driver,
                databaseId: driver.databaseId || "",
                driverLicenseClass: driver.driverLicenseClass || "",
                education: driver.education || driverConfigurationList.education || "",
                occupation: driver.occupation || driverConfigurationList.occupation || "",
                relationship: driver.id === 1 ? "Insured" : driver.relationship || driverConfigurationList.relationship || "",
                driverType: driver.id === 1 ? "Rated" : driver.driverType || driverConfigurationList.driverType || "",
                dlState: driver.dlState || driverConfigurationList.licenseState || "",
                sr22: driver.sr22 || driverConfigurationList.sR22 || "",
                sr22State: driver.sr22State || driverConfigurationList.sR22State || "",
                accidentsOrClaims: driver.accidentsOrClaims || driverConfigurationList.previousClaims,
                violations: (driver.accidentsOrClaims || driverConfigurationList.previousClaims) === "Yes" && (!driver.violations || driver.violations.length === 0)
                  ? [{ code: "", date: "", occurredOnSameDay: false }]
                  : driver.violations || [],
                yearsEmployed: driver.yearsEmployed || driverConfigurationList.yearsEmployed || "",
                monthsEmployed: driver.monthsEmployed || driverConfigurationList.monthsEmployed || "",
                licenseStatus: driver.licenseStatus || driverConfigurationList.licenseStatus || "",
                countryOfOrigin: driver.countryOfOrigin || driverConfigurationList.countryOfOrigin || "",
                internationalDriversLicense: driver.internationalDriversLicense || driverConfigurationList.internationalDriversLicense || "",
              }));
            });
          }
          setDriverLoading(false);
          return;
        }

        setDrivers((prev) => {
          let firstDriver = prev[0]; // 🔒 KEEP first driver from step1

          // If no first driver exists, return empty array
          if (!firstDriver) {
            return [];
          }

          // Check if any API driver matches the first driver's name
          const matchingApiDriver = driverList.find((apiDriver: any) => {
            const apiFirstName = (apiDriver.firstName ?? "").trim().toLowerCase();
            const apiLastName = (apiDriver.lastName ?? "").trim().toLowerCase();
            const firstDriverFirstName = (firstDriver.firstName ?? "").trim().toLowerCase();
            const firstDriverLastName = (firstDriver.lastName ?? "").trim().toLowerCase();

            return apiFirstName === firstDriverFirstName && apiLastName === firstDriverLastName;
          });

          // If a matching driver is found, merge API data into first driver (API data takes priority)
          if (matchingApiDriver) {
            const apiViolations = Array.isArray(matchingApiDriver.driverViolations) ? matchingApiDriver.driverViolations : [];
            const hasViolations = apiViolations.length > 0;
            const mergedViolations = hasViolations
              ? apiViolations.map((v: any) => ({
                  code: v.violationCode || v.code || "",
                  date: v.violationDate ? v.violationDate.split("T")[0] : v.date || "",
                  occurredOnSameDay: v.OccurredOnSameDayAsAnotherViolation ?? v.occurredOnSameDay ?? false,
                }))
              : firstDriver.violations || [];
            const mergedAccidentsOrClaims = hasViolations ? "Yes" : firstDriver.accidentsOrClaims || "No";
            firstDriver = {
              ...firstDriver,
              databaseId: matchingApiDriver.databaseId || "",
              dateOfBirth: matchingApiDriver.dateOfBirth ? matchingApiDriver.dateOfBirth.split("T")[0] : firstDriver.dateOfBirth || "",
              gender: matchingApiDriver.gender || firstDriver.gender || "",
              maritalStatus: matchingApiDriver.maritalStatus || firstDriver.maritalStatus || "",
              education: (matchingApiDriver.education ? EDUCATION_API_TO_UI_MAP[matchingApiDriver.education] || matchingApiDriver.education : "") || firstDriver.education || driverConfigurationList.education || "",
              occupation: (() => {
                const apiOccupation = Array.isArray(matchingApiDriver.occupations) && matchingApiDriver.occupations.length > 0 ? matchingApiDriver.occupations[0] : "";
                const isValidOccupation = apiOccupation && occupationOptions.some((opt) => opt.value === apiOccupation);
                return (isValidOccupation ? apiOccupation : "") || firstDriver.occupation || driverConfigurationList.occupation || "";
              })(),
              dlNumber: matchingApiDriver.licenseNumber || firstDriver.dlNumber || "",
              dlState: matchingApiDriver.licenseState || firstDriver.dlState || driverConfigurationList.licenseState || "",
              dlYear: matchingApiDriver.licenseYear ? matchingApiDriver.licenseYear.toString() : firstDriver.dlYear || "",
              accidentsOrClaims: mergedAccidentsOrClaims,
              violations:
                mergedAccidentsOrClaims === "Yes" && mergedViolations.length === 0
                  ? [{ code: "", date: "", occurredOnSameDay: false }]
                  : mergedViolations,
              sr22: firstDriver.sr22 || driverConfigurationList.sR22 || "",
              sr22State: firstDriver.sr22State || driverConfigurationList.sR22State || "",
              yearsEmployed: matchingApiDriver.yearsEmployed || firstDriver.yearsEmployed || driverConfigurationList.yearsEmployed || "",
              monthsEmployed: matchingApiDriver.monthsEmployed || firstDriver.monthsEmployed || driverConfigurationList.monthsEmployed || "",
              licenseStatus: matchingApiDriver.licenseStatus || firstDriver.licenseStatus || driverConfigurationList.licenseStatus || "",
              countryOfOrigin: matchingApiDriver.countryOfOrigin || firstDriver.countryOfOrigin || driverConfigurationList.countryOfOrigin || "",
              internationalDriversLicense: matchingApiDriver.internationalDriversLicense || firstDriver.internationalDriversLicense || driverConfigurationList.internationalDriversLicense || "",
            };
          }

          // If no match found and first driver has "Yes" but no violations, initialize one entry
          if (!matchingApiDriver && firstDriver.accidentsOrClaims === "Yes" && (!firstDriver.violations || firstDriver.violations.length === 0)) {
            firstDriver = { ...firstDriver, violations: [{ code: "", date: "", occurredOnSameDay: false }] };
          }

          // Filter out the matching driver from the API list (already merged into firstDriver)
          const filteredDriverList = driverList.filter((apiDriver: any) => {
            const apiFirstName = (apiDriver.firstName ?? "").trim().toLowerCase();
            const apiLastName = (apiDriver.lastName ?? "").trim().toLowerCase();
            const firstDriverFirstName = (firstDriver.firstName ?? "").trim().toLowerCase();
            const firstDriverLastName = (firstDriver.lastName ?? "").trim().toLowerCase();

            // Exclude if both first name AND last name match
            return !(apiFirstName === firstDriverFirstName && apiLastName === firstDriverLastName);
          });

          // Map filtered drivers from API as additional drivers (starting from id 2)
          const additionalDrivers = filteredDriverList.map((apiDriver: any, index: number) => {
            const apiViolations = Array.isArray(apiDriver.driverViolations) ? apiDriver.driverViolations : [];
            const hasViolations = apiViolations.length > 0;
            const additionalAccidentsOrClaims = hasViolations ? "Yes" : "No";
            const additionalViolations = hasViolations
              ? apiViolations.map((v: any) => ({
                  code: v.violationCode || v.code || "",
                  date: v.violationDate ? v.violationDate.split("T")[0] : v.date || "",
                  occurredOnSameDay: v.OccurredOnSameDayAsAnotherViolation ?? v.occurredOnSameDay ?? false,
                }))
              : [];
            return {
              id: index + 2, // start from 2
              isExpanded: false,
              databaseId: apiDriver.databaseId || "",
              firstName: apiDriver.firstName ?? "",
              lastName: apiDriver.lastName ?? "",
              dateOfBirth: apiDriver.dateOfBirth ? apiDriver.dateOfBirth.split("T")[0] : "",

              gender: apiDriver.gender ?? "",
              maritalStatus: apiDriver.maritalStatus ?? "",
              education: (apiDriver.education ? EDUCATION_API_TO_UI_MAP[apiDriver.education] || apiDriver.education : "") || driverConfigurationList.education || "",

              occupation: (() => {
                const apiOccupation = Array.isArray(apiDriver.occupations) && apiDriver.occupations.length > 0 ? apiDriver.occupations[0] : "";
                const isValidOccupation = apiOccupation && occupationOptions.some((opt) => opt.value === apiOccupation);
                return (isValidOccupation ? apiOccupation : "") || driverConfigurationList.occupation || "";
              })(),

              dlNumber: apiDriver.licenseNumber ?? "",
              dlState: apiDriver.licenseState || driverConfigurationList.licenseState || "",
              dlYear: apiDriver.licenseYear ? apiDriver.licenseYear.toString() : "",

              accidentsOrClaims: additionalAccidentsOrClaims,
              violations:
                additionalAccidentsOrClaims === "Yes" && additionalViolations.length === 0
                  ? [{ code: "", date: "", occurredOnSameDay: false }]
                  : additionalViolations,

              relationship: driverConfigurationList.relationship || "",
              driverType: apiDriver.excluded ? "Excluded" : driverConfigurationList.driverType || "Rated",
              sr22: driverConfigurationList.sR22 || "",
              sr22State: driverConfigurationList.sR22State || "",
              yearsEmployed: apiDriver.yearsEmployed || driverConfigurationList.yearsEmployed || "",
              monthsEmployed: apiDriver.monthsEmployed || driverConfigurationList.monthsEmployed || "",
              licenseStatus: apiDriver.licenseStatus || driverConfigurationList.licenseStatus || "",
              countryOfOrigin: apiDriver.countryOfOrigin || driverConfigurationList.countryOfOrigin || "",
              internationalDriversLicense: apiDriver.internationalDriversLicense || driverConfigurationList.internationalDriversLicense || "",
            };
          });

          const mergedDrivers = [firstDriver, ...additionalDrivers];

          setNextDriverId(mergedDrivers.length + 1);

          return mergedDrivers;
        });

        setDriverLoading(false);
      })

      .catch((error: any) => {
        setDriverLoading(false);
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response && error.response.status
                ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while searching the client!"}`
                : "Unknown error while getting  the driver list details!",
            life: 3000,
          });
        }
      });
  };
  const handleClearForm = () => {
    setDriverData({
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      gender: "",
      maritalStatus: "",
      education: "",
      occupation: "",
      dlNumber: "",
      dlState: "",
      dlYear: "",
      accidentsOrClaims: "",
      relationship: "",
      driverType: "",
      sr22: "",
      sr22State: "",
    });
  };

  // Generic input change handler for driver fields
  const handleDriverInputChange = (id: number, e: React.ChangeEvent<any>) => {
    const target = e.target;
    const name = target.name;
    let value = target.value;
    if (target.type === "checkbox") {
      value = target.checked ? "Yes" : "No";
    }

    // Clamp numeric fields to their valid ranges
    let clamped = value;
    if (name === "milesToWork" && value !== "") {
      const n = Math.floor(Number(value));
      clamped = String(Math.min(999, Math.max(0, n)));
    } else if (name === "annualMiles" && value !== "") {
      const n = Math.floor(Number(value));
      clamped = String(Math.min(1000000, Math.max(0, n)));
    } else if (name === "yearsEmployed" && value !== "") {
      const n = Math.floor(Number(value));
      clamped = String(Math.min(99, Math.max(0, n)));
    } else if (name === "monthsEmployed" && value !== "") {
      const n = Math.floor(Number(value));
      clamped = String(Math.min(11, Math.max(0, n)));
    }
    value = clamped;

    // Clear validation error for this field when user types
    const errorKey = `driver${id}.${name}`;
    if (validationErrors[errorKey]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }

    setDrivers((prev) =>
      prev.map((driver) => {
        if (driver.id !== id) return driver;
        const updated: any = { ...driver, [name]: value };
        // When accidentsOrClaims changes to "Yes", initialize violations array if empty
        if (name === "accidentsOrClaims" && value === "Yes") {
          if (!driver.violations || driver.violations.length === 0) {
            updated.violations = [{ code: driver.accidentViolationCode || "", date: driver.accidentViolationDate || "", occurredOnSameDay: false }];
          }
        }
        // When accidentsOrClaims changes to "No", clear violations
        if (name === "accidentsOrClaims" && value === "No") {
          updated.violations = [];
          updated.accidentViolationCode = "";
          updated.accidentViolationDate = "";
        }
        return updated;
      }),
    );
  };

  const handleViolationChange = (driverId: number, violationIndex: number, field: "code" | "date" | "occurredOnSameDay", value: string | boolean) => {
    // Clear validation error for this field
    const errorKey = `driver${driverId}.violations[${violationIndex}].${field}`;
    if (validationErrors[errorKey]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }

    setDrivers((prev) =>
      prev.map((driver) => {
        if (driver.id !== driverId) return driver;
        const violations = [...(driver.violations || [])];
        violations[violationIndex] = { ...violations[violationIndex], [field]: value };
        return { ...driver, violations };
      }),
    );
  };

  const addViolation = (driverId: number) => {
    setDrivers((prev) =>
      prev.map((driver) => {
        if (driver.id !== driverId) return driver;
        const violations = [...(driver.violations || []), { code: "", date: "", occurredOnSameDay: false }];
        return { ...driver, violations };
      }),
    );
  };

  const removeViolation = (driverId: number, violationIndex: number) => {
    setDrivers((prev) =>
      prev.map((driver) => {
        if (driver.id !== driverId) return driver;
        const violations = (driver.violations || []).filter((_, i) => i !== violationIndex);
        // If no violations left, set accidentsOrClaims to "No"
        if (violations.length === 0) {
          return { ...driver, violations, accidentsOrClaims: "No" };
        }
        return { ...driver, violations };
      }),
    );
  };

  const removeDriver = (id: number) => {
    if (drivers.length > 1) {
      setDrivers((prev) => prev.filter((driver) => driver.id !== id));
    }
  };

  return onNext || currentStep ? (
    <DriverContent
      drivers={drivers}
      setDrivers={setDrivers}
      handleDriverInputChange={handleDriverInputChange}
      removeDriver={removeDriver}
      handleAddDriver={handleAddDriver}
      handleClearForm={handleClearForm}
      handleViolationChange={handleViolationChange}
      addViolation={addViolation}
      removeViolation={removeViolation}
      isFirstStep={isFirstStep}
      onPrevious={onPrevious}
      onNext={onNext}
      driverLoading={driverLoading}
      validationErrors={validationErrors}
      validateForm={validateForm}
      toast={toast}
      loading={loading}
      selectedState={selectedState}
      policyType={workflowData?.step1?.policyType}
    />
  ) : (
    <Layout currentPage="Driver Information">
      <DriverContent
        drivers={drivers}
        setDrivers={setDrivers}
        handleDriverInputChange={handleDriverInputChange}
        removeDriver={removeDriver}
        handleAddDriver={handleAddDriver}
        handleClearForm={handleClearForm}
        handleViolationChange={handleViolationChange}
        addViolation={addViolation}
        removeViolation={removeViolation}
        isFirstStep={isFirstStep}
        onPrevious={onPrevious}
        onNext={onNext}
        driverLoading={driverLoading}
        validationErrors={validationErrors}
        validateForm={validateForm}
        toast={toast}
        loading={loading}
        selectedState={selectedState}
        policyType={workflowData?.step1?.policyType}
      />
    </Layout>
  );
}
